import { Dropdown, type OptionType } from '@omnipresentgroup/design-system';

import type { ColumnFiltersState, Table } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import type { MultiValue, SingleValue } from 'react-select';

type SelectValue = MultiValue<OptionType> | SingleValue<OptionType>;

interface OptionOverrideArguments<TData> {
  originalValue: string;
  count: number;
  originalRow?: TData;
}

type OptionOverride<TData> = (
  arguments_: OptionOverrideArguments<TData>,
) => Partial<OptionType>;

type Props<TData, TColumnHeaders extends Record<string, string>> = {
  table: Table<TData>; // The react-table instance
  columnId: keyof TColumnHeaders; // The ID of the column to filter by
  columnFilters: ColumnFiltersState; // Current column filters from react-table
  onFilterChange: (filters: ColumnFiltersState) => void; // Callback to update filters
  placeholder?: string; // Placeholder text for the dropdown
  label?: string; // Label for the dropdown
  showCounts?: boolean; // Whether to display the count of each unique value in the dropdown options
  isMulti?: boolean; // Whether the dropdown allows multiple selections
  optionOverride?: OptionOverride<TData>;
  initialOptions?: OptionType[]; // Optional fixed list of options (expected to have value=ID, label=Name)
  includeSubRows?: boolean; // Whether to include nested/subrows in filter options and counts (default: false)
};

/**
 * Generic component that renders a dropdown select to filter a specific column in a table.
 * It can derive options dynamically from table data or accept a predefined list.
 * Display labels can be customized via initialOptions or optionOverride.
 * Options are sorted by their final display label.
 * Handles duplicate display labels by showing them once but filtering by all underlying IDs.
 */
export const DropdownTableFilter = <
  TData,
  TColumnHeaders extends Record<string, string>,
>({
  table,
  columnId,
  columnFilters,
  onFilterChange,
  placeholder,
  label,
  showCounts = false,
  isMulti = false,
  optionOverride,
  initialOptions,
  includeSubRows = false,
}: Props<TData, TColumnHeaders>) => {
  // Get the row model once and reuse it throughout the component
  const rowModel = table.getCoreRowModel();
  const hasRows = rowModel && rowModel.flatRows.length > 0;

  // Determine which rows to use based on includeSubRows parameter
  const rowsToProcess = includeSubRows
    ? rowModel?.flatRows || []
    : rowModel?.rows || [];

  const uniqueFilterValues = useMemo(() => {
    if (initialOptions) {
      const valuesFromInitialOptions = initialOptions
        .map((opt) => String(opt.value))
        .filter(Boolean); // Ensure initial option values are also filtered if empty
      return [...new Set(valuesFromInitialOptions)].sort();
    }

    if (!hasRows) {
      return [];
    }

    const valuesFromTable = rowsToProcess
      .map((row) => {
        const value = row.getValue(columnId as string);
        return String(value || '');
      })
      .filter(Boolean); // This filters out null, undefined, ''
    return [...new Set(valuesFromTable)].sort();
  }, [initialOptions, hasRows, rowsToProcess, columnId]);

  const getValueCount = useCallback(
    (filterValue: string): number => {
      if (!showCounts || !filterValue || !hasRows) {
        return 0;
      }

      // Get all active filters except for the current column
      const otherFilters = columnFilters.filter(
        (filter) => filter.id !== columnId,
      );

      // Apply other filters first, then count rows matching this specific value
      const rowsForCounting = includeSubRows
        ? rowModel.flatRows
        : rowModel.rows || [];
      return rowsForCounting.filter((row) => {
        // First check if row passes all other active filters
        const passesOtherFilters = otherFilters.every((filter) => {
          const cellValue = row.getValue(filter.id);
          const filterValues = Array.isArray(filter.value)
            ? filter.value
            : [filter.value];
          return filterValues.some(
            (fv) => String(cellValue || '') === String(fv),
          );
        });

        if (!passesOtherFilters) {
          return false;
        }

        // Then check if this row matches the specific value we're counting
        const rowValue = row.getValue(columnId as string);
        return String(rowValue || '') === filterValue;
      }).length;
    },
    [showCounts, hasRows, columnFilters, columnId, rowModel, includeSubRows],
  );

  const options = useMemo((): OptionType[] => {
    const valueToFirstRowMap = new Map<string, TData>();
    if (!initialOptions && hasRows) {
      const rowsForMapping = includeSubRows
        ? rowModel.flatRows
        : rowModel.rows || [];
      for (const row of rowsForMapping) {
        const value = String(row.getValue(columnId as string) || '');
        if (value && !valueToFirstRowMap.has(value)) {
          // Ensure val is not empty
          valueToFirstRowMap.set(value, row.original as TData);
        }
      }
    }

    return uniqueFilterValues
      .map((originalValue) => {
        // Skip if originalValue is empty after filtering (should be redundant due to uniqueFilterValues but safe)
        if (!originalValue) {
          return null;
        }

        const count = getValueCount(originalValue);
        const representativeRow = initialOptions
          ? undefined
          : valueToFirstRowMap.get(originalValue);

        let displayLabel = originalValue;

        const foundInitialOption = initialOptions?.find(
          (opt) => String(opt.value) === originalValue,
        );
        if (foundInitialOption?.label) {
          displayLabel = foundInitialOption.label;
        }

        // Base option uses the originalValue as its 'value' prop
        const baseOption: OptionType = {
          value: originalValue,
          label: displayLabel,
        };

        let finalOption = { ...baseOption };

        if (optionOverride) {
          const overrideResult = optionOverride({
            originalValue,
            count,
            originalRow: representativeRow,
          });
          // Apply overrides, but crucially ensure 'value' is not changed from originalValue
          finalOption = {
            ...finalOption,
            ...overrideResult,
            value: originalValue, // Value must remain the original ID/value
          };
        }

        // Add count suffix to the final label (either original or overridden)
        if (showCounts) {
          finalOption.label = `${finalOption.label} (${count})`;
        }

        return finalOption;
      })
      .filter((option): option is OptionType => option !== null) // Filter out any nulls from explicit skips
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [
    uniqueFilterValues,
    getValueCount,
    showCounts,
    optionOverride,
    initialOptions,
    hasRows,
    rowModel,
    columnId,
    includeSubRows,
  ]);

  const currentFilter = columnFilters.find((filter) => filter.id === columnId);

  const selectedValue = useMemo((): SelectValue => {
    const filterValue = currentFilter?.value;

    if (isMulti) {
      const currentFilterValues = Array.isArray(filterValue)
        ? filterValue.map(String)
        : [];
      if (currentFilterValues.length === 0) {
        return [];
      }
      return options.filter((opt) =>
        currentFilterValues.includes(String(opt.value)),
      );
    }
    if (filterValue === undefined || filterValue === null) {
      return null;
    }
    const currentFilterString = String(filterValue);
    return (
      options.find((opt) => String(opt.value) === currentFilterString) || null
    );
  }, [currentFilter, options, isMulti]);

  const handleSelectChange = (newValue: SelectValue) => {
    const newFilters = columnFilters.filter((f) => f.id !== columnId);

    if (newValue) {
      if (isMulti) {
        const selectedOptions = newValue as MultiValue<OptionType>;
        const values = selectedOptions
          .map((opt) => String(opt.value))
          .filter(Boolean);
        if (values.length > 0) {
          newFilters.push({ id: columnId as string, value: values });
        }
      } else {
        const selectedOption = newValue as SingleValue<OptionType>;
        const value = selectedOption?.value;
        if (value !== undefined && value !== null && String(value) !== '') {
          newFilters.push({ id: columnId as string, value: String(value) });
        }
      }
    }
    onFilterChange(newFilters);
  };

  return (
    <Dropdown
      id={`${String(columnId)}-dropdown-filter`}
      isMulti={isMulti}
      value={selectedValue}
      onChange={handleSelectChange}
      options={options}
      placeholder={
        placeholder || `Search ${label?.toLowerCase() || String(columnId)}`
      }
      label={label}
      isSearchable
      isClearable
    />
  );
};
